import React from "react";
import classNames from "classnames";
import ReactMarkdown from "markdown-to-jsx";

import Image from "components/imageNew/image";
import { formatDateEventCard } from "components/eventCard/utils";

import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";

import {
  Breakfast,
  BreakfastPartnership,
  ModeratorShortBlock,
} from "content-ui/breakfast/types";

import Carousel from "./carousel/carousel";
import { AttendeesList } from "./attendeesList/attendeesList";
import PartnershipBlock from "../partnershipBlock/partnershipBlock";

type EventInfoProps = Breakfast & {
  isPrevious: boolean;
};

const EventInfo = ({
  agenda,
  dateTime,
  infoBlockHeading,
  infoBlockText,
  isPrevious,
  locationAddress,
  locationLink,
  images,
  moderators,
  speakers,
  attendees,
  timezone,
  partnership,
}: EventInfoProps) => {
  const { track } = useMixpanelTracking();

  const handleClickOnLocation = async () => {
    await track("Breakfast location link click", {
      "Click link": locationLink,
      "Click text": locationAddress,
    });
  };

  const handleClickOnUserContact = async (moderator: ModeratorShortBlock) => {
    await track("Breakfast user link click", {
      "Click link": moderator.link,
      "Click text": moderator.name,
    });
  };

  const handlePartnerClick = async (partner: BreakfastPartnership) => {
    await track("Breakfast partner link click", {
      Component: "EventInfo",
      "Click link": partner.url,
      "Click text": partner.name,
    });
  };

  return (
    <div className={"flex flex-col gap-10 text-black-rock"}>
      <div className={"flex flex-col gap-10 lg:flex-row"}>
        <div className={"flex flex-col"}>
          <h3 className={"mb-[10px] font-bold text-[16px] leading-[130%]"}>
            On the agenda
          </h3>
          <div className={""}>
            <ReactMarkdown
              options={{ forceBlock: true }}
              className={classNames(
                "text-[16px] leading-[130%] text-mono-70 [&_p]:m-0",
                "[&_a]:inline [&_a]:underline [&_a]:[text-underline-position:under]",
                "focus:[&_a]:outline-none focus-visible:[&_a]:outline-none focus-visible:[&_a]:ring",
                "[&_a]:text-cerise focus-visible:[&_a]:ring-cerise"
              )}
            >
              {agenda}
            </ReactMarkdown>
          </div>
        </div>
        {!isPrevious ? (
          <div className={"flex flex-none flex-col gap-[10px] lg:w-[330px]"}>
            <h3 className={"font-bold text-[16px] leading-[130%]"}>
              Date & Venue
            </h3>
            <p className={"text-[16px] leading-[130%] text-mono-70"}>
              {formatDateEventCard(new Date(dateTime), { timezone: timezone })}
            </p>
            <p className={"text-[16px] leading-[130%] text-mono-70"}>
              {locationLink ? (
                <a
                  href={locationLink}
                  className={
                    "leading-[130%] text-cerise underline [text-underline-position:under]"
                  }
                  target={"_blank"}
                  onClick={handleClickOnLocation}
                  rel="noreferrer"
                >
                  {locationAddress}
                </a>
              ) : (
                <span>{locationAddress}</span>
              )}
            </p>
            {partnership && (
              <PartnershipBlock
                {...partnership}
                onClick={async () => handlePartnerClick(partnership)}
              />
            )}
          </div>
        ) : (
          <>
            {images.length > 0 ? (
              <div className={"flex-none lg:min-h-[360px] lg:w-[580px]"}>
                <Carousel slides={images} />
              </div>
            ) : null}
          </>
        )}
      </div>
      <div className={"flex flex-col gap-[30px]"}>
        <div className={"flex flex-col"}>
          <h3 className={"mb-[10px] font-bold text-[16px] leading-[130%]"}>
            Moderators & Guest speakers
          </h3>
          <ul
            aria-label={"Moderators & Guest speakers list"}
            className={
              "grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 lg:gap-[30px]"
            }
          >
            {[...moderators, ...speakers].map((moderator, index) => {
              return (
                <li
                  className={"flex flex-row gap-[10px]"}
                  key={`${moderator.id}-${index}`}
                  aria-label={"Moderator or guest speaker"}
                >
                  <div className={"flex-none"}>
                    {moderator.image && moderator.image.url && (
                      <Image
                        src={moderator.image.url}
                        alt={
                          moderator.image.alt || `Photo of ${moderator.name}`
                        }
                        width={moderator.image.width}
                        height={moderator.image.height}
                        blurDataURL={undefined}
                        className={
                          "pointer-events-none h-[70px] w-[70px] object-cover [clip-path:polygon(0_19%,12%_0,100%_0,100%_81%,88%_100%,0_100%)]"
                        }
                        sizes={"300px"}
                      />
                    )}
                  </div>
                  <div className={"flex flex-col gap-1  text-mono-70"}>
                    {moderator.link ? (
                      <a
                        href={moderator.link}
                        target={"_blank"}
                        className={
                          "font-bold text-[14px] leading-[130%] underline transition-colors [text-underline-position:under] hover:text-cerise"
                        }
                        rel="noreferrer"
                        onClick={async () => {
                          await handleClickOnUserContact(moderator);
                        }}
                      >
                        {moderator.name}
                      </a>
                    ) : (
                      <p className={"font-bold text-[14px] leading-[130%]"}>
                        {moderator.name}
                      </p>
                    )}
                    {(moderator.role || moderator.company) && (
                      <p className={"text-[14px] leading-[130%]"}>
                        {moderator.role}{" "}
                        {moderator.company && `@ ${moderator.company}`}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        {attendees.length > 0 && isPrevious && (
          <AttendeesList list={attendees} />
        )}
      </div>
      {!isPrevious && (infoBlockText || infoBlockHeading) ? (
        <div
          className={"flex flex-col gap-[10px] bg-white p-[14px]"}
          data-testid={"eventInfo__infoBlock"}
        >
          {infoBlockHeading && (
            <h3
              className={classNames("font-bold text-[16px] leading-[130%]", {})}
            >
              {infoBlockHeading}
            </h3>
          )}
          {infoBlockText && (
            <div>
              <ReactMarkdown
                options={{ forceBlock: true }}
                className={classNames(
                  "text-[16px] leading-[130%] text-mono-70 text-mono-70 [&_p]:m-0",
                  "[&_ol]:list-decimal [&_ol]:pl-[24px]",
                  "[&_a]:inline [&_a]:underline [&_a]:[text-underline-position:under]",
                  "focus:[&_a]:outline-none focus-visible:[&_a]:outline-none focus-visible:[&_a]:ring",
                  "[&_a]:text-cerise focus-visible:[&_a]:ring-cerise"
                )}
              >
                {infoBlockText}
              </ReactMarkdown>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default EventInfo;
